import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import {
  BillingInformation,
  Invoice,
  Session,
  useGenerateInvoiceForSession,
  useSessionInvoices,
} from '@app/api';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import {
  DateInput,
  Input,
  InvoiceStatusChip,
  SubmitButton,
  SubmitButtonsLine,
  TextArea,
  TextLoadingPlaceholder,
  useToast,
} from 'components';
import { useState } from 'react';
import { Form, Formik } from 'formik';
import moment from 'moment';

const useStyles = createUseStyles((theme: Theme) => ({
  list: {
    display: 'flex',
    alignItems: 'stretch',
    gap: theme.marginBase,
    marginBottom: theme.marginBase * 2,
    flexWrap: 'wrap',
  },
  invoice: {
    minHeight: 150,
    maxWidth: 150,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: theme.marginBase,
    padding: theme.marginBase * 2,
    cursor: 'pointer',

    border: {
      style: 'solid',
      width: 1,
      color: theme.colors.grayBorder,
      radius: theme.borderRadius.large,
    },
  },
  disabled: {
    opacity: 0.6,
  },
  createInvoice: {
    ...theme.fonts.h5,
    borderStyle: 'dashed',
    backgroundColor: theme.colors.grayBackground,
    justifyContent: 'center',
  },
  selected: {
    borderColor: theme.colors.greenAccessible,
    backgroundColor: theme.colors.greenMuted,
  },
  invoiceName: {
    ...theme.fonts.h5,
  },
  invoiceDate: {
    ...theme.fonts.caption,
  },
  spacer: {
    flex: 1,
  },
  invoiceAmount: {
    ...theme.fonts.label,
  },
  invoiceCreationInstructions: {
    marginBottom: theme.marginBase * 2,
  },
  buttons: {
    marginTop: theme.marginBase * 2,
  },
  invoiceDateInput: {
    maxWidth: theme.marginBase * 25,
    accentColor: theme.colors.accentAccessible,
    colorScheme: 'light',
  },
}));

interface Props {
  selected?: Invoice;
  onSelect: (invoice?: Invoice) => void;
  session: Session;
  billingInformation: BillingInformation;
}

export const InvoiceSelection = ({
  session,
  selected,
  onSelect,
  billingInformation,
}: Props) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const toast = useToast();

  const dateFormat = Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'short',
  });

  const { data: invoices, isLoading } = useSessionInvoices(session.id);
  const { mutate: generateInvoice, isLoading: isCreationLoading } =
    useGenerateInvoiceForSession(session?.id, session?.organizationId);
  const [isCreating, setIsCreating] = useState<boolean>(false);

  const select = (invoice: Invoice) => {
    if (isCreationLoading) {
      return;
    }
    setIsCreating(false);
    onSelect(invoice);
  };

  const createInvoice = (purchaseOrder?: string, invoiceDate?: string) => {
    generateInvoice(
      {
        purchaseOrder,
        invoiceDate,
        metadata: {
          sessionName: session?.internalName || session?.title || '',
        },
      },
      {
        onSuccess: (invoice) => {
          onSelect(invoice);
          setIsCreating(false);
        },
        onError: (e) => toast.exception(e),
      },
    );
  };

  const selectCreateInvoice = () => {
    if (isLoading) {
      return;
    }
    onSelect(undefined);
    setIsCreating(true);
  };

  const today = moment().format('YYYY-MM-DD');

  return (
    <>
      <div className={classes.list}>
        {invoices?.map((invoice) => (
          <div
            className={classNames(classes.invoice, {
              [classes.selected]: invoice.id === selected?.id,
              [classes.disabled]: isCreationLoading,
            })}
            aria-checked={invoice.id === selected?.id}
            role="radio"
            onClick={() => select(invoice)}
          >
            <div className={classes.invoiceName}>{invoice.name}</div>
            <InvoiceStatusChip status={invoice.status} />
            <div className={classes.invoiceDate}>
              {dateFormat.format(
                moment(invoice.openAt || invoice.createdAt).toDate(),
              )}
            </div>
            <div className={classes.spacer} />
            {!!invoice.tax && invoice.totalHT && (
              <div className={classes.invoiceAmount}>
                {t('admin.billing.invoicing.totalExclTax', {
                  amount: invoice.totalHT,
                })}
              </div>
            )}
            {!!invoice.creditUsageTotal && (
              <div className={classes.invoiceAmount}>
                {t('admin.billing.invoicing.creditUsageTotal', {
                  amount: invoice.creditUsageTotal * -1,
                })}
              </div>
            )}
            {invoice.total !== undefined && (
              <div className={classes.invoiceAmount}>
                {t('admin.billing.invoicing.totalInclTax', {
                  amount: invoice.total,
                })}
              </div>
            )}
          </div>
        ))}
        {isCreationLoading || isLoading ? (
          <div
            className={classNames(classes.invoice, {
              [classes.selected]: !isLoading,
            })}
            aria-checked
            role="radio"
          >
            <div className={classes.invoiceName}>
              <TextLoadingPlaceholder sample="000031" />
            </div>
            <div className={classes.invoiceDate}>
              <TextLoadingPlaceholder sample={dateFormat.format(new Date())} />
            </div>
            <div className={classes.spacer} />
            <div className={classes.invoiceAmount}>
              <TextLoadingPlaceholder
                sample={t('admin.billing.invoicing.totalInclTax', {
                  amount: 20000,
                })}
              />
            </div>
          </div>
        ) : (
          <div
            className={classNames(classes.invoice, classes.createInvoice, {
              [classes.disabled]: isLoading,
              [classes.selected]: isCreating,
            })}
            onClick={selectCreateInvoice}
          >
            {t('admin.billing.invoicing.createNewInvoice')}
          </div>
        )}
      </div>

      {isCreating && (
        <div className={classes.invoiceCreationInstructions}>
          <Formik
            initialValues={{ purchaseOrder: '', invoiceDate: today }}
            onSubmit={(values) =>
              createInvoice(
                values.purchaseOrder || undefined,
                values.invoiceDate || undefined,
              )
            }
          >
            <Form>
              {billingInformation.billingInstructions && (
                <TextArea
                  label={t(
                    'admin.billing.billingInfo.finalVerification.billingInstructions',
                  )}
                  rows={5}
                  disabled
                  value={billingInformation.billingInstructions}
                />
              )}
              <DateInput
                name="invoiceDate"
                label={t('admin.billing.invoicing.invoiceDate')}
                max={today}
                containerClass={classes.invoiceDateInput}
              />
              {billingInformation.requirePurchaseOrderNumber && (
                <Input
                  name="purchaseOrder"
                  label={t('admin.billing.invoicing.purchaseOrder')}
                />
              )}
              <SubmitButtonsLine className={classes.buttons}>
                <SubmitButton
                  small
                  loading={isCreationLoading}
                  text={t('admin.billing.invoicing.createInvoiceButton')}
                />
              </SubmitButtonsLine>
            </Form>
          </Formik>
        </div>
      )}
    </>
  );
};
